import { defineStore } from 'pinia';
import axiosInstance from '@/plugins/axios';

const BASE = '/api/access';

/**
 * Who sees what. One overview call feeds the whole card; every toggle is
 * optimistic — the pill flips immediately and flips back if the server says
 * no (it says no when someone tries to strip the last super-admin).
 */
export const useAccessStore = defineStore('access', {
    state: () => ({
        staff: [],
        roles: [],
        permissions: [],
        searchResults: [],
        loading: false,
        searching: false,
        error: null,
    }),

    actions: {
        flash(message) {
            this.error = message;
            clearTimeout(this._flashTimer);
            this._flashTimer = setTimeout(() => { this.error = null; }, 5000);
        },

        async fetchOverview() {
            this.loading = true;
            try {
                const { data } = await axiosInstance.get(`${BASE}/overview`);
                this.staff = data.data.staff ?? [];
                this.roles = data.data.roles ?? [];
                this.permissions = data.data.permissions ?? [];
            } catch (error) {
                this.flash(error?.response?.status === 403
                    ? 'Samo super-admin može upravljati pristupom.'
                    : 'Pristup se nije mogao učitati.');
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async search(term) {
            if (!term || term.trim().length < 2) {
                this.searchResults = [];
                return;
            }
            this.searching = true;
            try {
                const { data } = await axiosInstance.get(`${BASE}/users`, { params: { search: term.trim() } });
                const listed = new Set(this.staff.map((user) => user.id));
                this.searchResults = (data.data ?? []).filter((user) => !listed.has(user.id));
            } finally {
                this.searching = false;
            }
        },

        /** Pull a search hit into the staff list so their pills can be clicked. */
        addToStaff(user) {
            if (!this.staff.some((existing) => existing.id === user.id)) {
                this.staff.push(user);
            }
            this.searchResults = [];
        },

        async toggleRole(user, role) {
            const had = user.roles.includes(role.id);
            user.roles = had
                ? user.roles.filter((id) => id !== role.id)
                : [...user.roles, role.id];

            try {
                const url = `${BASE}/users/${user.id}/roles/${role.id}`;
                const { data } = had
                    ? await axiosInstance.delete(url)
                    : await axiosInstance.post(url);
                user.roles = data.data.roles ?? user.roles;
            } catch (error) {
                user.roles = had
                    ? [...user.roles, role.id]
                    : user.roles.filter((id) => id !== role.id);
                this.flash(error?.response?.data?.message ?? 'Promjena uloge nije uspjela.');
            }
        },

        async togglePermission(role, permission) {
            const had = role.permissions.includes(permission.id);
            role.permissions = had
                ? role.permissions.filter((id) => id !== permission.id)
                : [...role.permissions, permission.id];

            try {
                const url = `/api/roles/${role.id}/permissions/${permission.id}`;
                if (had) await axiosInstance.delete(url);
                else await axiosInstance.put(url);
            } catch (error) {
                role.permissions = had
                    ? [...role.permissions, permission.id]
                    : role.permissions.filter((id) => id !== permission.id);
                this.flash(error?.response?.data?.message ?? 'Promjena permisije nije uspjela.');
            }
        },
    },
});
